<script setup lang="ts">
// The ONE bet slip, used everywhere. The sportsbook book (pages/index.vue) and
// the scoreboard odds widget (components/OddsBoard.vue) both mount this, so the
// slip looks and behaves identically wherever a wager is started. The parent
// owns a single useBetSlip() instance — so its odds grid and this slip share one
// selection — and passes it in via :slip; this component is just presentation.
import type { useBetSlip } from '~/composables/useBetSlip'

const props = defineProps<{
  slip: ReturnType<typeof useBetSlip>
  // host hook to refresh anything it shows after a successful bet (e.g. the
  // book's recent-bets list); the scoreboard passes nothing.
  onPlaced?: () => void | Promise<void>
}>()

const { fmtNum, fmtMoney } = useClientFormat()
// the instance's refs/computeds are created once by the parent and never
// reassigned, so destructuring here keeps them reactive.
const {
  sel, stake, placing, potential, toWin, signedIn, balance, canPlace, placeBet, clearSel,
  pickingVenue, pickerKey, playingAt, confirmVenue, cancelVenue, changeVenue,
} = props.slip

function place() { placeBet(props.onPlaced) }
</script>

<template>
  <div class="slip">
    <h3>bet slip</h3>
    <div v-if="!sel" class="slip-empty">tap a price to start a wager</div>
    <template v-else>
      <div class="slip-pick">
        <div class="slip-match">{{ sel.ev.away_team ? sel.ev.away_team + ' @ ' : '' }}{{ sel.ev.home_team }}</div>
        <div class="slip-sel">{{ sel.label }}</div>
        <div class="slip-odds">{{ fmtNum(sel.odds) }}</div>
      </div>
      <template v-if="signedIn">
        <label class="fld-lbl">stake (BMO)</label>
        <input v-model.number="stake" type="number" min="1" step="1" class="fld" placeholder="0" />
        <div class="slip-row"><span>potential return</span><b class="mono">{{ fmtNum(potential) }}</b></div>
        <div class="slip-row"><span>to win</span><b class="mono accent">{{ fmtNum(toWin) }}</b></div>
        <button class="btn-primary" :disabled="!canPlace" @click="place">
          {{ placing ? 'placing…' : 'place bet' }}
        </button>
        <!-- the remembered "where are you playing?" answer (venue or home) for
             this TTL window; change reopens the picker without placing -->
        <div v-if="playingAt" class="slip-site">
          {{ playingAt }} · <button class="slip-site-change" @click="changeVenue">change</button>
        </div>
        <div v-if="balance !== null" class="slip-bal mono">{{ fmtMoney(balance) }} BMO to wager</div>
      </template>
      <template v-else>
        <p class="slip-note">Sign in to place this wager.</p>
        <button class="btn-primary" @click="place">sign in to bet</button>
      </template>
      <button class="btn-link" @click="clearSel">clear</button>
    </template>

    <!-- BMO venue picker: opens between the place-bet tap and the request when no
         choice is remembered (or via "change"). Keyed per open so each open is a
         fresh instance. Teleports itself to <body>, so it renders the same from the
         book column, the scoreboard modal and the mobile sticky slip. -->
    <VenuePicker v-if="pickingVenue" :key="pickerKey" purpose="bmo_bet"
                 @select="confirmVenue" @close="cancelVenue" />
  </div>
</template>

<style scoped>
/* Only the slip's inner detail classes live here. The container (.slip,
   .slip-empty), the stake field (.fld, .fld-lbl), buttons (.btn-primary,
   .btn-link) and .mono are global in assets/css/main.css, so the slip renders
   the same in the book column and in the scoreboard modal. */
.slip-pick { background: var(--panel-2); border: 1px solid var(--line); border-radius: 10px; padding: 12px; margin-bottom: 14px; }
.slip-match { font-size: 12px; color: var(--muted); }
.slip-sel { font-weight: 700; margin-top: 2px; }
.slip-odds { font-family: var(--font-mono); color: var(--accent); font-weight: 700; margin-top: 4px; }
.slip-row { display: flex; justify-content: space-between; font-size: 13px; color: var(--muted); margin: 8px 0; }
.slip-row .accent { color: var(--accent); }
.slip-bal { margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--line); font-size: 11px; color: var(--muted); text-align: right; }
.slip-note { color: var(--muted); font-size: 12px; line-height: 1.5; margin: 4px 0 12px; }
.slip-site { margin-top: 10px; font-size: 11px; color: var(--muted); text-align: center; font-family: var(--font-mono); }
.slip-site-change { background: none; color: var(--accent); font-size: 11px; padding: 0; text-decoration: underline; }
.slip-site-change:hover { filter: brightness(1.1); }
</style>
